import {Field, FieldProps} from 'formik';
import React, {useState} from 'react';
import {InferenceAppConfigFields} from './InferenceAppConfigFields';
import {useIntl} from 'react-intl';
import {
  getInputSourceOptions,
  SPA_PROD_VIDEO_SOURCES,
  SPA_VIDEO_SOURCES,
} from '../TestDeployView/useDeploy';
import {StudioSelect} from '../../base-components/StudioSelect';
import {InputSource} from '../../types/deployment/InferenceInputSource';
import {StreamingDevice} from '../../types/deployment/StreamingDeviceResponse';
import {InferenceAppFieldsBaseProps, InferenceAppForm} from './InferenceAppTypes';
import {NodeSource} from '../../api/deployment/DeploymentNodes';

export type StreamConfigPanelProps = InferenceAppFieldsBaseProps & {
  streamingDevices: StreamingDevice[];
  nodeSources: NodeSource[];
};

export const StreamConfigPanel = ({
  application,
  inProgress,
  values,
  spa,
  projectPostProcessors,
  streamingDevices,
  nodeSources,
  viewMode,
  handlePostProcessorChange,
  onCheckboxChanged,
  setReady,
  gspList,
}: StreamConfigPanelProps) => {
  const intl = useIntl();
  const [appConfigFieldsReady, setAppConfigFieldsReady] = useState(false);

  const isProduction = spa.target.type === 'PRODUCTION';
  const sourceTypes = application?.inputSources;
  const userDataSourceTypes = getInputSourceOptions(sourceTypes, (is: InputSource) =>
    isProduction ? SPA_PROD_VIDEO_SOURCES.has(is) : SPA_VIDEO_SOURCES.has(is)
  );
  const ipCameras = streamingDevices.filter(device => device.type === 'IP_CAMERA');
  const isOnnxWeb = application.runtimeType === 'ONNX_RUNTIME_WEB';

  const checkReadiness = () => {
    setReady(
      appConfigFieldsReady &&
        !!values.inferenceSource &&
        (values.inferenceSource !== 'IP_CAMERA' || !!values.ipCamera) &&
        (values.inferenceSource !== 'DEVICE_CAMERA' || !!values.deviceCamera)
    );
  };

  const updateAppConfigFieldsReadiness = (value: boolean) => {
    setAppConfigFieldsReady(value);
    checkReadiness();
  };

  return (
    <div className="inference-app__fields">
      <InferenceAppConfigFields
        application={application}
        inProgress={inProgress}
        allowAPIExposure={false}
        values={values}
        spa={spa}
        postProcessorRequired={isOnnxWeb}
        projectPostProcessors={projectPostProcessors}
        viewMode={viewMode}
        handlePostProcessorChange={handlePostProcessorChange}
        onCheckboxChanged={onCheckboxChanged}
        setReady={updateAppConfigFieldsReadiness}
        gspList={gspList}
      />
      {values.exposeInferencePage && (
        <div className="inference-app__panel__container__form">
          <Field name="inferenceSource">
            {({field}: FieldProps<InferenceAppForm['inferenceSource']>) => {
              return (
                <StudioSelect
                  fullWidth
                  label={intl.formatMessage({id: 'test.selectDataSource'})}
                  options={userDataSourceTypes}
                  onChange={event => {
                    field.onChange(event);
                    checkReadiness();
                  }}
                  disabled={inProgress}
                  SelectProps={field}
                />
              );
            }}
          </Field>
          {values.inferenceSource === 'IP_CAMERA' && (
            <Field name="ipCamera">
              {({field}: FieldProps<InferenceAppForm['ipCamera']>) => {
                return (
                  <StudioSelect
                    fullWidth
                    label={intl.formatMessage({id: 'createDeploy.externalSource'})}
                    options={ipCameras.reduce(
                      (options, item) => ({...options, [item.id]: item.name}),
                      {}
                    )}
                    disabled={inProgress || !ipCameras.length}
                    onChange={event => {
                      field.onChange(event);
                      checkReadiness();
                    }}
                    placeholder={intl.formatMessage({
                      id: ipCameras.length
                        ? 'deployments.selectIpCamera'
                        : 'deployments.noIpCameras',
                    })}
                    disablePlaceholder
                    SelectProps={field}
                  />
                );
              }}
            </Field>
          )}
          {values.inferenceSource === 'DEVICE_CAMERA' && (
            <Field name="deviceCamera">
              {({field}: FieldProps<InferenceAppForm['deviceCamera']>) => {
                return (
                  <StudioSelect
                    fullWidth
                    label={intl.formatMessage({id: 'createDeploy.deviceSource'})}
                    options={nodeSources.reduce(
                      (options, item) => ({...options, [item.sourceId]: item.sourceName}),
                      {}
                    )}
                    disabled={inProgress || !nodeSources.length}
                    onChange={event => {
                      field.onChange(event);
                      checkReadiness();
                    }}
                    placeholder={intl.formatMessage({
                      id: ipCameras.length
                        ? 'deployments.selectIpCamera'
                        : 'deployments.noIpCameras',
                    })}
                    disablePlaceholder
                    SelectProps={field}
                  />
                );
              }}
            </Field>
          )}
        </div>
      )}
    </div>
  );
};
